import { useState } from 'react';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useSelectionMode } from './useSelectionMode';

type Params<T extends { id: string }> = {
  records: T[];
  entityName: 'file' | 'document';
  nameOf: (record: T) => string;
  deleteRecord: (record: T) => Promise<unknown>;
  downloadRecord: (record: T) => Promise<unknown> | unknown;
  refetch: () => Promise<unknown> | unknown;
  logContext: Record<string, unknown>;
};

export function useBulkDocumentSelection<T extends { id: string }>({
  records,
  entityName,
  nameOf,
  deleteRecord,
  downloadRecord,
  refetch,
  logContext,
}: Params<T>) {
  const selection = useSelectionMode();
  // Snapshotted so the confirmation copy stays stable if the selection changes while open
  const [bulkDeleteRecords, setBulkDeleteRecords] = useState<T[] | null>(null);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  const selectedRecords = records.filter(record => selection.selectedIds.has(record.id));

  const requestBulkDelete = () => setBulkDeleteRecords(selectedRecords);
  const cancelBulkDelete = () => setBulkDeleteRecords(null);

  const confirmBulkDelete = async () => {
    if (!bulkDeleteRecords) return;
    setIsBulkDeleting(true);
    const failedNames: string[] = [];
    let lastError: unknown = null;
    for (const record of bulkDeleteRecords) {
      try {
        await deleteRecord(record);
      } catch (e) {
        failedNames.push(nameOf(record));
        lastError = e;
      }
    }
    setIsBulkDeleting(false);
    setBulkDeleteRecords(null);
    selection.exit();
    await refetch();
    if (failedNames.length > 0) {
      notifyAndLogError(
        `Failed to delete ${entityName}s`,
        `An error occurred while trying to delete ${failedNames.join(', ')}. Please try again later.`,
        lastError,
        { ...logContext, failedNames },
      );
    }
  };

  const downloadSelected = async () => {
    setIsBulkDownloading(true);
    try {
      for (const record of selectedRecords) {
        await downloadRecord(record);
      }
    } finally {
      setIsBulkDownloading(false);
    }
  };

  return {
    selection,
    selectedRecords,
    bulkDeleteNames: bulkDeleteRecords ? bulkDeleteRecords.map(nameOf) : null,
    requestBulkDelete,
    cancelBulkDelete,
    confirmBulkDelete,
    isBulkDeleting,
    isBulkDownloading,
    downloadSelected,
  };
}
